import { DrawingFetchFailureReason } from "../../schema";
import { hasDrawingCode } from "../../services/drawingCodes";

export interface DrawingFailure {
  filename: string;
  reason: DrawingFetchFailureReason;
  alreadyAttached: boolean;
}

export interface DrawingLine {
  purchaseRequestItemId: string;
  code: string;
  found: boolean;
  complete: boolean;
  truncated: boolean;
  attached: string[];
  subDrawings: string[];
  failed: DrawingFailure[];
}

export interface DrawingCodeConflict {
  purchaseRequestItemId: string;
  name: string;
  codes: string[];
}

export interface DrawingSearchResultData {
  lines: DrawingLine[];
  notFoundCodes: string[];
  skippedCodes: string[];
  conflicts: DrawingCodeConflict[];
  incomplete: boolean;
  pendingPurchaseRequestItemIds: string[];
}

export interface DrawingItemLike {
  name?: string | null;
  code?: string | null;
}

// the search is useful only when at least one line carries a product code,
// in its Item code or written into its name
export function hasProductCode(items: DrawingItemLike[]): boolean {
  return items.some((item) => hasDrawingCode(item.name, item.code));
}

const REASON_TEXT: { [reason: string]: string } = {
  NOT_FOUND: "no longer on the drawing source",
  TRUNCATED: "arrived incomplete",
  TOO_LARGE: "exceeds the attachment size limit",
};

export function describeFailureReason(reason: DrawingFetchFailureReason) {
  return REASON_TEXT[reason] || "could not be fetched";
}

export interface FailureNote {
  filename: string;
  text: string;
  // true: file is on the line, only its sub-drawing check was skipped (mild)
  isNote: boolean;
}

// every drawing is expected as a DWG, a PDF and a STEP; .stp counts as STEP
const REQUIRED_FORMATS: { label: string; extensions: string[] }[] = [
  { label: "DWG", extensions: ["dwg"] },
  { label: "PDF", extensions: ["pdf"] },
  { label: "STEP", extensions: ["step", "stp"] },
];

export interface MissingFormats {
  number: string;
  formats: string[];
}

// as the server's leadingToken: "22000111" from "22000111_1.step" or
// "22000111 - Company.pdf"
function drawingNumberOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  const stem = dot > 0 ? filename.slice(0, dot) : filename;

  return stem.split(/[ _]/)[0];
}

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");

  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

/**
 * Drawing numbers found with only some of the required formats, in the
 * order first seen. A file that was found but failed to attach still counts
 * as present: it exists, and its failure is reported on its own.
 */
export function findMissingFormats(filenames: string[]): MissingFormats[] {
  const byNumber = new Map<string, Set<string>>();

  filenames.forEach((filename) => {
    const number = drawingNumberOf(filename);
    const extensions = byNumber.get(number) || new Set<string>();

    extensions.add(extensionOf(filename));
    byNumber.set(number, extensions);
  });

  const missing: MissingFormats[] = [];

  byNumber.forEach((extensions, number) => {
    const formats = REQUIRED_FORMATS.filter(
      (format) => !format.extensions.some((ext) => extensions.has(ext)),
    ).map((format) => format.label);

    if (formats.length > 0) {
      missing.push({ number, formats });
    }
  });

  return missing;
}

export interface DrawingDisplayRow {
  key: string;
  code: string;
  attached: string[];
  subDrawings: string[];
  failures: FailureNote[];
  truncated: boolean;
  missingFormats: MissingFormats[];
}

// counts and rows are derived from the lists, never from `found`
export function buildDrawingRows(
  result: DrawingSearchResultData,
): DrawingDisplayRow[] {
  const pending = new Set(result.pendingPurchaseRequestItemIds);

  return result.lines
    .filter(
      (line) =>
        line.attached.length > 0 ||
        line.subDrawings.length > 0 ||
        line.failed.length > 0 ||
        line.truncated,
    )
    .map((line) => ({
      key: line.purchaseRequestItemId,
      code: line.code,
      attached: line.attached,
      subDrawings: line.subDrawings,
      truncated: line.truncated,
      // a cut-short walk may simply not have reached the other formats yet
      missingFormats:
        line.truncated || pending.has(line.purchaseRequestItemId)
          ? []
          : findMissingFormats([
              ...line.attached,
              ...line.subDrawings,
              ...line.failed.map((failure) => failure.filename),
            ]),
      failures: line.failed.map((failure) => ({
        filename: failure.filename,
        isNote: failure.alreadyAttached,
        text: failure.alreadyAttached
          ? `${
              failure.filename
            } is attached, but its sub-drawings were not checked (${describeFailureReason(
              failure.reason,
            )})`
          : `${failure.filename} was not attached (${describeFailureReason(
              failure.reason,
            )})`,
      })),
    }));
}

interface ErrorLike {
  message: string;
  extensions?: { [key: string]: unknown };
}

export interface DrawingSearchErrorInput {
  message: string;
  graphQLErrors?: ReadonlyArray<ErrorLike>;
  networkError?: unknown;
}

// GraphQL errors normally arrive in graphQLErrors; Apollo 2 puts those of a
// non-2xx response into networkError.result.errors instead
function firstGraphQLError(error: DrawingSearchErrorInput): ErrorLike | null {
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0];
  }

  const networkError = error.networkError as
    | { result?: { errors?: unknown } }
    | null
    | undefined;
  const errors =
    networkError && networkError.result && networkError.result.errors;

  if (Array.isArray(errors) && errors.length > 0 && errors[0]) {
    return errors[0] as ErrorLike;
  }

  return null;
}

export function getDrawingSearchErrorMessage(
  error: DrawingSearchErrorInput,
): string {
  const graphQLError = firstGraphQLError(error);
  const code = graphQLError?.extensions?.code;

  switch (code) {
    case "DRAWING_SEARCH_IN_PROGRESS":
      return "A drawing search is already running for this purchase request. Please wait for it to finish.";
    case "DRAWINGS_UNAVAILABLE":
      // the only code whose server text is user-safe
      return graphQLError && typeof graphQLError.message === "string"
        ? graphQLError.message
        : "The drawing source is unavailable.";
    case "FORBIDDEN":
      return "You are not allowed to search drawings for this purchase request.";
    case "UNAUTHENTICATED":
      return "Your session has expired. Please log in again.";
    default:
      if (error.networkError && !graphQLError) {
        return "The connection was lost or timed out before the search finished. Some drawings may already have been attached; press Drawings again to continue (nothing will be duplicated).";
      }

      return "The drawing search failed. Please try again.";
  }
}
